import { Async } from '../../components/Async.tsx';
import { BarList } from '../../components/charts/BarList.tsx';
import { TrendChart } from '../../components/charts/TrendChart.tsx';
import { DataTable, type Column, type TotalsRow } from '../../components/DataTable.tsx';
import { ImmatureCohortNotice } from '../../components/ImmatureCohortNotice.tsx';
import { EmptyState, Panel } from '../../components/States.tsx';
import { formatDate, formatNumber, formatPercent } from '../../lib/format.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';
import { usePageTitle } from '../../lib/page-title.ts';
import { useMarketing, useMarketingTrend, type MarketingResponse } from '../../lib/queries.ts';
import { useDashboardParams } from '../../lib/url-state.ts';
import { Controls } from '../controls/Controls.tsx';

type Row = MarketingResponse['data']['rows'][number];

export function MarketingPage() {
  const t = useT();
  const locale = useLocale();
  usePageTitle(t.marketing.title);
  const [params] = useDashboardParams();
  const marketing = useMarketing(params);
  const trend = useMarketingTrend(params);
  const n = (value: number): string => formatNumber(value, locale);
  const sourceName = (row: { name: string | null }): string =>
    row.name ?? t.marketing.unknownSource;

  return (
    <>
      <h1 className="text-2xl font-bold tracking-tight">{t.marketing.title}</h1>
      <Controls meta={marketing.data?.meta} compare={false} />
      <Panel title={t.marketing.sources}>
        <Async query={marketing}>
          {({ data }) => {
            if (data.rows.length === 0)
              return <EmptyState title={t.common.noData} description={t.marketing.empty} />;
            const columns: Column<Row>[] = [
              {
                id: 'source',
                header: t.marketing.columns.source,
                sortValue: (r) => sourceName(r.source),
                cell: (r) => sourceName(r.source),
              },
              {
                id: 'leads',
                header: t.marketing.columns.leadsNew,
                align: 'right',
                sortValue: (r) => r.leadsNew,
                cell: (r) => n(r.leadsNew),
              },
              {
                id: 'deals',
                header: t.marketing.columns.dealsWon,
                align: 'right',
                sortValue: (r) => r.dealsWon,
                cell: (r) => n(r.dealsWon),
              },
              {
                id: 'containers',
                header: t.marketing.columns.containersSold,
                align: 'right',
                sortValue: (r) => r.containersSold,
                cell: (r) => n(r.containersSold),
              },
              {
                id: 'cohort',
                header: t.marketing.columns.cohortSize,
                help: t.marketing.cohortPeriod(
                  t.period.range(
                    formatDate(data.cohortPeriod.from),
                    formatDate(data.cohortPeriod.to),
                  ),
                ),
                align: 'right',
                sortValue: (r) => r.cohort.size,
                cell: (r) => n(r.cohort.size),
              },
              {
                id: 'qualified',
                header: t.marketing.columns.qualifiedRate,
                align: 'right',
                sortValue: (r) => r.cohort.qualifiedRate,
                cell: (r) => formatPercent(r.cohort.qualifiedRate, locale),
              },
              {
                id: 'won',
                header: t.marketing.columns.wonRate,
                align: 'right',
                sortValue: (r) => r.cohort.wonRate,
                cell: (r) => formatPercent(r.cohort.wonRate, locale),
              },
            ];
            const totals: TotalsRow = {
              label: t.common.total,
              cells: {
                leads: n(data.totals.leadsNew),
                deals: n(data.totals.dealsWon),
                containers: n(data.totals.containersSold),
              },
            };
            return (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t.marketing.cohortPeriod(
                    t.period.range(
                      formatDate(data.cohortPeriod.from),
                      formatDate(data.cohortPeriod.to),
                    ),
                  )}
                </p>
                <ImmatureCohortNotice maturity={data.cohortMaturity} />
                <DataTable
                  caption={t.marketing.sources}
                  columns={columns}
                  rows={data.rows}
                  rowKey={(r) => r.source.id ?? 'unknown'}
                  defaultSort={{ id: 'leads', direction: 'desc' }}
                  totals={totals}
                />
                {data.notAvailable.length > 0 ? (
                  <p className="text-sm text-muted-foreground">{t.marketing.notAvailable}</p>
                ) : null}
              </div>
            );
          }}
        </Async>
      </Panel>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t.marketing.mix}>
          <Async query={marketing}>
            {({ data }) => (
              <BarList
                ariaLabel={t.marketing.mix}
                unit={t.units.leads}
                items={data.rows.map((r) => ({
                  key: r.source.id,
                  label: sourceName(r.source),
                  value: r.leadsNew,
                }))}
              />
            )}
          </Async>
        </Panel>
        <Panel title={t.marketing.trend}>
          <Async query={trend}>
            {({ data }) => (
              <>
                <p className="mb-2 text-xs text-muted-foreground">
                  {t.granularity[data.granularity]}
                </p>
                <TrendChart
                  title={t.marketing.trend}
                  stacked
                  series={data.series.map((s) => ({
                    key: s.source.id,
                    label: sourceName(s.source),
                    points: s.points,
                  }))}
                />
              </>
            )}
          </Async>
        </Panel>
      </div>
    </>
  );
}
